import { sectionTypeMatches, useAgentDefinitions, usePlugins } from '@tunasong/plugin-lib'
import type { BlockAction } from '@tunasong/plugin-lib'
import { useThunkDispatch } from '@tunasong/redux'
import type { BlockRef } from '@tunasong/schemas'
import { useAgentChildren } from './use-agent-children.js'
import { setActiveInlineTurn, startInlineAgentTurn } from './inline/inline-agent-turn.js'
import { resolveBlockAnchorEl } from './inline/resolve-block-anchor.js'
import { openInlineAsk } from './inline/inline-ask.js'
import { getAgentDisplayName } from './agent-visuals.js'

/**
 * Aggregate per-block affordances for a given `BlockRef`. Combines:
 *
 *  - agent-contributed `AgentDefinition.blockAffordances` for every agent
 *    currently attached to the host entity (matched by `agent.definitionId`),
 *    filtered by `blockTypes` (with `'*'` meaning any kind); affordances with
 *    `sectionTypes` are additionally gated by `block.sectionType` (fail-open
 *    via `sectionTypeMatches`);
 *  - generic `TunaPlugin.getBlockActions(block)` contributions from every
 *    registered plugin (e.g., chord plugin's own "Transpose").
 *
 * Agent items dispatch `openAgentComposer` on select — surfacing the Agents
 * sidebar focused on the chosen agent with the targeted block preset as the
 * composer's focus and an optional draft pre-filled from
 * `buildAsk(block).messageTemplate`. Plugin items use their own `onSelect`.
 *
 * Pure read hook: no side effects on mount, no fetches. Re-renders when the
 * underlying graph cache, plugin set, or agent definitions change.
 */
export const useBlockActions = (block: BlockRef): BlockAction[] => {
  const attachedAgents = useAgentChildren(block.entityId)
  const agentDefs = useAgentDefinitions()
  const plugins = usePlugins('all')
  const dispatch = useThunkDispatch()

  const fromAgents = attachedAgents.flatMap<BlockAction>(agent => {
    const definitionId = agent.definitionId
    if (definitionId === undefined) {
      return []
    }
    const def = agentDefs.find(d => d.id === definitionId)
    const affordances = def?.blockAffordances ?? []
    return affordances
      .filter(a => a.blockTypes === '*' || a.blockTypes.includes(block.type))
      .filter(a => sectionTypeMatches(a, block))
      .map(a => ({
        id: `${agent.id}/${a.id}`,
        label: a.label,
        iconKey: a.iconKey,
        onSelect: () => {
          const { messageTemplate, focus } = a.buildAsk(block)
          // No canned ask: open the inline ask field at the block for the musician to type into,
          // rather than a dead click (spec InlineAskField). Templated actions stay one-click.
          if (!messageTemplate) {
            openInlineAsk({
              agentId: agent.id,
              agentName: getAgentDisplayName(agent),
              focus,
              trigger: { type: 'change', reason: a.id },
              anchorEl: resolveBlockAnchorEl(block.id),
            })
            return
          }
          // In-context: run an interactive inline turn and register it so the
          // answer/edit renders inline at the block — never the sidebar.
          const { requestId } = startInlineAgentTurn(
            { dispatch },
            { agentId: agent.id, message: messageTemplate, focus, trigger: { type: 'change', reason: a.id } }
          )
          setActiveInlineTurn({ agentId: agent.id, requestId, anchorEl: resolveBlockAnchorEl(block.id) })
        },
      }))
  })

  const fromPlugins = (plugins ?? []).flatMap(p => p.getBlockActions?.(block) ?? [])

  return [...fromAgents, ...fromPlugins]
}
